/**
 * Cash Flow — how much cash there is today, by the ledger's word, and what the loans take out of
 * it over the next 30, 60 and 90 days. Phase 5, the first slice of FINANCE.md §9.
 *
 * Every figure comes from `GET /api/finance/cash`: the balances QuickBooks last reported for the
 * entity's bank and credit-card accounts, summed by `cashPosition`, and the debt service summed
 * from the loan schedules by `debtService`. This screen adds nothing up. There is no bank feed
 * yet, and the screen says so: the balance is as of the sync that carried it, not the bank's
 * word this minute. Inflows (reservations, payouts) and other outflows arrive with the forecast
 * engine; until then the horizons show debt service only, labelled as such.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dollarsToCents } from '@sixgen/core';
import { api, type CashResponse, type ForecastBacktestResponse } from '../../api.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';
import { formatMoney } from '../../money.js';

export function CashFlowPage(): ReactNode {
  const entityId = useEntityId();
  const entities = useEntities();

  const cash = useQuery({
    queryKey: ['finance', 'cash', entityId],
    enabled: entityId !== null,
    queryFn: () => api.get<CashResponse>(`/api/finance/cash?entity=${entityId}`),
  });

  // How the forecast has done: re-run from past month-starts with what was booked by then.
  const backtest = useQuery({
    queryKey: ['finance', 'forecast-backtest', entityId],
    enabled: entityId !== null,
    queryFn: () =>
      api.get<ForecastBacktestResponse>(`/api/finance/forecast-backtest?entity=${entityId}`),
  });

  const queryClient = useQueryClient();
  const [reserveError, setReserveError] = useState<string | null>(null);
  const setReserve = useMutation({
    mutationFn: (body: unknown) => api.put('/api/finance/reserve-policy', body),
    onSuccess: () => {
      setReserveError(null);
      void queryClient.invalidateQueries({ queryKey: ['finance', 'cash'] });
    },
    onError: (err: unknown) => setReserveError(err instanceof Error ? err.message : String(err)),
  });

  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;
  const data = cash.data;
  const latestBalance = data?.accounts
    .map((a) => a.balance_as_of)
    .filter((d): d is string => d !== null)
    .sort()
    .at(-1);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Cash Flow</h1>
          <p className="subtitle">
            Cash as QuickBooks last reported it, and the debt service the loan schedules put in
            front of it. Code sums; the forecast below is labelled, and its record is at the bottom.
          </p>
        </div>
      </header>

      <div className="filters">
        <EntityPicker />
      </div>

      {(cash.isLoading || (entityId === null && entities.isLoading)) && (
        <p className="muted">Loading…</p>
      )}
      {cash.isError && <p className="error">Could not load cash: {cash.error.message}</p>}
      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {data && (
        <>
          <section className="card">
            <h2>Cash today</h2>
            <dl className="kv">
              <dt>Cash in bank accounts</dt>
              <dd className="num">{formatMoney(data.position.cash_cents)}</dd>
              <dt>Owed on credit cards</dt>
              <dd className="num">{formatMoney(data.position.credit_card_cents)}</dd>
              <dt>Net</dt>
              <dd className="num">
                <strong>{formatMoney(data.position.net_cents)}</strong>
              </dd>
            </dl>
            <p className="muted small">
              {data.position.label} · {data.position.bank_accounts} bank{' '}
              {data.position.bank_accounts === 1 ? 'account' : 'accounts'},{' '}
              {data.position.credit_cards} credit{' '}
              {data.position.credit_cards === 1 ? 'card' : 'cards'}
              {data.position.unknown_balances > 0 &&
                ` · ${data.position.unknown_balances} with no reported balance, not summed`}
              {latestBalance
                ? ` · balances as QuickBooks reported them, last carried ${new Date(latestBalance).toLocaleString()}`
                : ' · no balance has been carried from QuickBooks yet — run a sync'}
              . No bank feed is connected; bank vs ledger arrives with Plaid.
            </p>
            {data.accounts.length > 0 && (
              <table className="table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Kind</th>
                    <th className="num">Balance as reported</th>
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.map((a) => (
                    <tr key={a.account_id} className={a.is_active ? '' : 'muted'}>
                      <td>
                        {a.name}
                        {!a.is_active && <span className="chip-tag">inactive</span>}
                      </td>
                      <td className="muted">
                        {a.kind === 'bank' ? 'bank' : 'credit card'}
                        {a.account_subtype && ` · ${a.account_subtype}`}
                      </td>
                      <td className="num">
                        {a.current_balance_cents === null ? (
                          <span className="muted">not reported</span>
                        ) : (
                          formatMoney(a.current_balance_cents)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className={data.reserve?.check.breached ? 'card alert-card' : 'card'}>
            <h2>Cash floor</h2>
            {data.reserve ? (
              <>
                <p>
                  Keep at least{' '}
                  <strong>{formatMoney(data.reserve.policy.minimum_cash_cents)}</strong>.{' '}
                  {data.reserve.check.breached && data.reserve.check.first ? (
                    <span className="chip chip-attention">
                      Under the floor within {data.reserve.check.first.days} days: base case{' '}
                      {formatMoney(data.reserve.check.first.base_cents)} by{' '}
                      {data.reserve.check.first.to},{' '}
                      {formatMoney(data.reserve.check.first.shortfall_cents)} short
                    </span>
                  ) : (
                    <span className="chip chip-ok">Holds across every horizon</span>
                  )}
                  {data.reserve.check.opening_breached && (
                    <span className="chip chip-attention"> Already under it today</span>
                  )}
                </p>
                <p className="muted small">
                  Checked daily; a breach publishes CASH_RESERVE_THRESHOLD_BREACHED, which the CEO
                  briefing reads. {data.reserve.check.calc_method}.
                </p>
              </>
            ) : (
              <p className="muted">
                No cash floor set for this entity. Set one to be told when the forecast falls under
                it.
              </p>
            )}
            <form
              className="inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (!entityId) return;
                const typed = String(new FormData(event.currentTarget).get('minimum') ?? '');
                try {
                  setReserve.mutate({
                    entityId,
                    minimumCashCents: dollarsToCents(typed).toString(),
                  });
                } catch (err) {
                  setReserveError(err instanceof Error ? err.message : String(err));
                }
              }}
            >
              <label>
                Minimum cash ($){' '}
                <input name="minimum" required inputMode="decimal" placeholder="25,000.00" />
              </label>
              <button type="submit" disabled={setReserve.isPending}>
                {setReserve.isPending
                  ? 'Saving…'
                  : data.reserve
                    ? 'Change the floor'
                    : 'Set the floor'}
              </button>
            </form>
            {reserveError && <p className="error">{reserveError}</p>}
          </section>

          <section className="card">
            <h2>Where cash will be</h2>
            <p className="muted small">
              {data.forecast.label}: today&apos;s net cash, plus the host payouts of confirmed stays
              checking in within each horizon ({data.forecast.bookings_considered} considered), plus
              what the same window received a year ago after this point — its late bookings — scaled
              by this year&apos;s pace against last year&apos;s book at the same lead time, held
              between 50% and 150% ({data.forecast.prior_year_stays_considered} prior-year stays
              read
              {data.forecast.prior_year_stays_considered === 0
                ? ': no prior year, so nothing is expected beyond the confirmed book'
                : ''}
              ); less the loans&apos; debt service and a trailing {data.forecast.trailing_months}
              -month average of operating expense (
              {formatMoney(data.forecast.operating_monthly_cents)} a month), and the planned CapEx
              items still open ({data.forecast.capex_considered}) at their budget on their planned
              date. Upside and downside move only the payouts. Not in it: taxes, distributions,
              properties added or sold since last year.
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>Horizon</th>
                  <th>Through</th>
                  <th className="num">Confirmed</th>
                  <th className="num">Expected</th>
                  <th className="num">Pace</th>
                  <th className="num">Debt service</th>
                  <th className="num">Operating</th>
                  <th className="num">Planned CapEx</th>
                  <th className="num">Open repairs</th>
                  <th className="num">Base</th>
                  <th className="num">Upside</th>
                  <th className="num">Downside</th>
                </tr>
              </thead>
              <tbody>
                {data.forecast.horizons.map((h) => (
                  <tr key={h.days} className={h.days > 90 ? 'muted' : ''}>
                    <th scope="row">
                      {h.days === 180
                        ? 'Six months'
                        : h.days === 365
                          ? 'One year'
                          : `${h.days} days`}
                    </th>
                    <td>{h.to}</td>
                    <td className="num" title={`${h.booking_count} stays`}>
                      {formatMoney(h.confirmed_cents)}
                    </td>
                    <td
                      className="num"
                      title={`Last year this window received ${formatMoney(h.prior_year_final_cents)}, ${formatMoney(h.prior_year_on_books_cents)} of it booked by this lead time`}
                    >
                      {formatMoney(h.expected_cents)}
                    </td>
                    <td className="num">
                      {h.pace_bps === null ? '—' : `${Number(h.pace_bps) / 100}%`}
                    </td>
                    <td className="num">{formatMoney(h.debt_service_cents)}</td>
                    <td className="num">{formatMoney(h.operating_cents)}</td>
                    <td className="num" title={`${h.capex_count} planned items`}>
                      {formatMoney(h.capex_cents)}
                    </td>
                    <td
                      className="num"
                      title={`${h.maintenance_count} open tickets with an estimate`}
                    >
                      {formatMoney(h.maintenance_cents)}
                    </td>
                    <td className="num">
                      <strong>{formatMoney(h.base_cents)}</strong>
                    </td>
                    <td className="num">{formatMoney(h.upside_cents)}</td>
                    <td className="num">{formatMoney(h.downside_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="card">
            <h2>Debt service ahead</h2>
            <p className="muted small">
              From {data.active_loans} active {data.active_loans === 1 ? 'loan' : 'loans'} on the
              Debt screen, by their schedules: principal, interest and escrow due from {data.as_of}.
              Taxes, distributions and other outflows are not here; the inflows are in the forecast
              above.
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>Horizon</th>
                  <th>Through</th>
                  <th className="num">Payments</th>
                  <th className="num">Principal</th>
                  <th className="num">Interest</th>
                  <th className="num">Escrow</th>
                  <th className="num">Total out</th>
                  <th className="num">Cash after debt service</th>
                </tr>
              </thead>
              <tbody>
                {data.debt_service.map((h) => (
                  <tr key={h.days}>
                    <th scope="row">{h.days} days</th>
                    <td>{h.to}</td>
                    <td className="num">{h.payment_count}</td>
                    <td className="num">{formatMoney(h.principal_cents)}</td>
                    <td className="num">{formatMoney(h.interest_cents)}</td>
                    <td className="num">{formatMoney(h.escrow_cents)}</td>
                    <td className="num">{formatMoney(h.total_cents)}</td>
                    <td className="num">{formatMoney(h.cash_after_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {backtest.data && (
            <section className="card">
              <h2>How the forecast has done</h2>
              <p className="muted small">
                {backtest.data.label}: the payouts leg of the forecast re-run from each of the last{' '}
                {backtest.data.months} month-starts whose ninety days have elapsed (
                {backtest.data.as_of_dates.length} of them), using only what was booked by that day
                and the prior year at that day&apos;s pace, against what actually landed. A positive
                error ran high. &ldquo;Confirmed alone&rdquo; is what the first version, without
                seasonality and pace, would have missed by. The cash leg follows the payouts.
              </p>
              {backtest.data.horizons.length === 0 ? (
                <p className="muted">
                  Nothing to test yet: no month-start&apos;s ninety days have elapsed with stays on
                  the books.
                </p>
              ) : (
                <>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Horizon</th>
                        <th className="num">Months</th>
                        <th className="num">Mean miss</th>
                        <th className="num">Bias</th>
                        <th className="num">Mean miss, %</th>
                        <th className="num">Confirmed alone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backtest.data.horizons.map((h) => (
                        <tr key={h.days}>
                          <th scope="row">{h.days} days</th>
                          <td className="num">{h.snapshots}</td>
                          <td className="num">{formatMoney(h.mean_abs_error_cents)}</td>
                          <td className="num">{formatMoney(h.mean_error_cents)}</td>
                          <td className="num">
                            {h.mean_abs_pct_error === null
                              ? '—'
                              : `${(h.mean_abs_pct_error * 100).toFixed(1)}%`}
                          </td>
                          <td className="num">{formatMoney(h.confirmed_mean_abs_error_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>From</th>
                        <th className="num">Days</th>
                        <th className="num">Confirmed then</th>
                        <th className="num">Expected then</th>
                        <th className="num">Forecast</th>
                        <th className="num">Landed</th>
                        <th className="num">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backtest.data.snapshots.map((s) => (
                        <tr key={`${s.as_of}:${s.days}`} className={s.days === 30 ? '' : 'muted'}>
                          <th scope="row">{s.as_of}</th>
                          <td className="num">{s.days}</td>
                          <td className="num">{formatMoney(s.confirmed_cents)}</td>
                          <td className="num">{formatMoney(s.expected_cents)}</td>
                          <td className="num">{formatMoney(s.forecast_cents)}</td>
                          <td className="num">{formatMoney(s.actual_cents)}</td>
                          <td className="num">
                            {formatMoney(s.error_cents)}
                            {s.error_ratio !== null && (
                              <span className="muted small">
                                {' '}
                                ({(s.error_ratio * 100).toFixed(1)}%)
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <h3>Cash</h3>
              <p className="muted small">
                {backtest.data.cash.label}: the closing cash the forecast would have said from each
                month-start, opening from the net position that held that morning, against the
                position that actually held at the end of each horizon. Balances come from every
                version of each account QuickBooks has sent since{' '}
                {backtest.data.cash.history_starts_at === null
                  ? 'the first sync (none yet)'
                  : backtest.data.cash.history_starts_at.slice(0, 10)}
                , so a date before that is not covered and is counted, not guessed. Planned CapEx
                and repair estimates are left out: neither is kept as of a past date. &ldquo;Nothing
                changes&rdquo; is what assuming tomorrow&apos;s cash equals today&apos;s would have
                missed by.
              </p>
              {backtest.data.cash.horizons.length === 0 ? (
                <p className="muted">
                  Nothing to test yet: the balance history ({backtest.data.cash.balance_versions}{' '}
                  {backtest.data.cash.balance_versions === 1 ? 'version' : 'versions'}) does not
                  cover a month-start and its ninety days
                  {backtest.data.cash.uncovered_snapshots > 0 &&
                    ` (${backtest.data.cash.uncovered_snapshots} uncovered)`}
                  .
                </p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Horizon</th>
                      <th className="num">Months</th>
                      <th className="num">Mean miss</th>
                      <th className="num">Bias</th>
                      <th className="num">Nothing changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backtest.data.cash.horizons.map((h) => (
                      <tr key={h.days}>
                        <th scope="row">{h.days} days</th>
                        <td className="num">{h.snapshots}</td>
                        <td className="num">{formatMoney(h.mean_abs_error_cents)}</td>
                        <td className="num">{formatMoney(h.mean_error_cents)}</td>
                        <td className="num">{formatMoney(h.naive_mean_abs_error_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
